import { copyMarkdownToClipboard, SlackishMarkdown } from '@platform/markdown';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  Tooltip,
  Typography,
} from '@shared/ui';
import React from 'react';
import { MarkdownEditor } from '../MarkdownEditor/MarkdownEditor';
import './EditableSummary.css';
import '../../../styles/cards.css';

export interface EditableSummaryProps {
  value: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  showEditButton?: boolean;
  // Optional streaming chunks to render incrementally while in summary mode
  // New chunks are appended in order for display; when entering edit mode,
  // the chunks are aggregated into a single document for editing.
  chunks?: string[];
  // Optional callback invoked after committing edits; parent can clear
  // any underlying streaming/chunk state at this point.
  onSaveCommit?: () => void;
  // NEW: Async save handler that persists edits to backend
  onSave?: (markdown: string) => Promise<void>;
  onEditStart?: () => void;
  onCancelEdit?: () => void;
  onCopy?: () => void;
  // NEW: Loading states for save operation
  isSaving?: boolean;
  isLoadingRevision?: boolean;
  saveError?: string | null;
  // NEW: External control of editing state
  isEditing?: boolean;
  onEditingChange?: (isEditing: boolean) => void;
  // NEW: Render header externally
  renderHeader?: (props: {
    isEditing: boolean;
    isDirty: boolean;
    onToggleEdit: () => void;
    onCopy: () => void;
    copyTooltipTitle: string;
    readOnly: boolean;
    aggregatedForDisplay: string;
  }) => React.ReactNode;
  // Feedback props
  feedback?: {
    artifactId: string;
    artifactType:
      | 'coaching_session_summary'
      | 'coaching_arc_summary'
      | 'coaching_feedback'
      | string;
    latestFeedbackType: 'positive' | 'negative' | null;
    onOpen: (type: 'positive' | 'negative') => void;
  };
}

export const EditableSummary: React.FC<EditableSummaryProps> = ({
  value,
  onChange,
  readOnly = false,
  showEditButton = true,
  chunks = [],
  onSaveCommit,
  onSave,
  onEditStart,
  onCancelEdit,
  onCopy,
  isSaving = false,
  isLoadingRevision = false,
  saveError = null,
  isEditing: externalIsEditing,
  onEditingChange,
  renderHeader,
  feedback,
}) => {
  const [internalIsEditing, setInternalIsEditing] = React.useState(false);
  const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;
  const setIsEditing = React.useCallback(
    (newValue: boolean) => {
      if (externalIsEditing === undefined) {
        setInternalIsEditing(newValue);
      }
      onEditingChange?.(newValue);
    },
    [externalIsEditing, onEditingChange]
  );
  const [draftValue, setDraftValue] = React.useState(value);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [initialDraft, setInitialDraft] = React.useState<string>('');
  const [copyState, setCopyState] = React.useState<'idle' | 'copied' | 'error'>('idle');

  // Aggregate streaming chunks for display in summary mode.
  // We compute this on render to avoid drifting state; when entering edit mode
  // we snapshot the current aggregate into the draft.
  const aggregatedForDisplay = React.useMemo(() => {
    if (isEditing) return draftValue;
    if (!chunks || chunks.length === 0) return value;
    // Concatenate all chunks onto the base value
    return value + chunks.join('');
  }, [isEditing, draftValue, value, chunks]);

  // Compare against captured baseline
  const isDirty = React.useMemo(() => {
    if (!isEditing) return false;
    // Only dirty if we have an initialDraft set AND it differs from current
    if (!initialDraft) return false;
    return draftValue !== initialDraft;
  }, [isEditing, draftValue, initialDraft]);

  const handleDoubleClick = () => {
    if (!readOnly && !isEditing) {
      // Start editing from the fully aggregated text
      const baseline = !chunks || chunks.length === 0 ? value : value + chunks.join('');
      setDraftValue(baseline);
      setInitialDraft(baseline);
      setIsEditing(true);
      onEditStart?.();
    }
  };

  const handlePreviewKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleDoubleClick();
    }
  };

  const handleSaveChanges = async () => {
    if (onSave) {
      try {
        await onSave(draftValue);
      } catch {
        // Error handled by parent, keep editing mode open
        return; // Don't close edit mode on error
      }
    }

    if (onChange) {
      onChange(draftValue);
    }

    // Signal to parent that the committed value should replace any streaming state
    if (onSaveCommit) {
      onSaveCommit();
    }

    setInitialDraft(''); // Reset when saving successfully
    setIsEditing(false); // Only close on success
  };

  const handleCancelClick = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmDiscard = () => {
    // Revert editor to the last non-edit aggregated value
    setDraftValue(value);
    setInitialDraft(''); // Reset when discarding
    setIsEditing(false);
    setShowConfirmModal(false);
    onCancelEdit?.();
  };

  const handleCancelDiscard = () => {
    setShowConfirmModal(false);
  };

  const handleToggleEdit = () => {
    if (!isEditing) {
      // Entering edit: seed draft from the current aggregated summary
      const baseline = !chunks || chunks.length === 0 ? value : value + chunks.join('');
      setDraftValue(baseline);
      setInitialDraft(baseline);
      setIsEditing(true);
      onEditStart?.();
    } else {
      // Leaving edit without explicit save — show confirm modal
      setShowConfirmModal(true);
    }
  };

  React.useEffect(() => {
    if (copyState === 'idle') return;
    const timeout = window.setTimeout(() => setCopyState('idle'), 2000);
    return () => window.clearTimeout(timeout);
  }, [copyState]);

  // Sync draft value when value changes externally (only when not editing)
  React.useEffect(() => {
    if (!isEditing) {
      setDraftValue(value);
    }
  }, [value, isEditing]);

  // Initialize editing state when externally controlled editing starts
  const prevIsEditingRef = React.useRef(isEditing);
  React.useEffect(() => {
    if (isEditing && !prevIsEditingRef.current && externalIsEditing !== undefined) {
      const baseline = !chunks || chunks.length === 0 ? value : value + chunks.join('');
      setDraftValue(baseline);
      setInitialDraft(baseline);
    }
    prevIsEditingRef.current = isEditing;
  }, [isEditing, externalIsEditing, value, chunks]);

  // Call parent onChange when draftValue changes during editing
  React.useEffect(() => {
    if (isEditing && onChange) {
      onChange(draftValue);
    }
  }, [isEditing, draftValue, onChange]);

  const handleCopy = React.useCallback(async () => {
    if (!aggregatedForDisplay) return;
    try {
      await copyMarkdownToClipboard(aggregatedForDisplay);
      setCopyState('copied');
      onCopy?.();
    } catch {
      setCopyState('error');
    }
  }, [aggregatedForDisplay, onCopy]);

  const copyTooltipTitle =
    copyState === 'copied' ? 'Copied!' : copyState === 'error' ? 'Copy failed' : 'Copy summary';

  const defaultHeader = (
    <div className="editable-summary-header">
      {isEditing ? (
        <>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              handleSaveChanges();
            }}
            disabled={!isDirty || isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
          <div style={{ flex: 1 }} />
          <Button
            variant="text"
            color="primary"
            onClick={handleCancelClick}
            disabled={isSaving || isLoadingRevision}
            sx={{ minWidth: 40, padding: '4px 8px' }}
          >
            <Icon>close</Icon>
          </Button>
        </>
      ) : (
        <>
          <Tooltip title={copyTooltipTitle}>
            <span>
              <IconButton
                aria-label="Copy summary to clipboard"
                size="small"
                onClick={handleCopy}
                disabled={!aggregatedForDisplay}
              >
                <Icon fontSize="small">content_copy</Icon>
              </IconButton>
            </span>
          </Tooltip>
          {feedback && (
            <>
              <Tooltip title="What works well">
                <span>
                  <IconButton
                    aria-label="Thumbs up"
                    size="small"
                    onClick={() => feedback.onOpen('positive')}
                    sx={{
                      color:
                        feedback.latestFeedbackType === 'positive' ? 'primary.main' : 'inherit',
                    }}
                  >
                    <Icon fontSize="small">
                      {feedback.latestFeedbackType === 'positive' ? 'thumb_up' : 'thumb_up_off_alt'}
                    </Icon>
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="What could be improved">
                <span>
                  <IconButton
                    aria-label="Thumbs down"
                    size="small"
                    onClick={() => feedback.onOpen('negative')}
                    sx={{
                      color:
                        feedback.latestFeedbackType === 'negative' ? 'primary.main' : 'inherit',
                    }}
                  >
                    <Icon fontSize="small">
                      {feedback.latestFeedbackType === 'negative'
                        ? 'thumb_down'
                        : 'thumb_down_off_alt'}
                    </Icon>
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
          <Tooltip title={readOnly ? 'Summary is still being generated...' : 'Edit summary'}>
            <span>
              <IconButton
                aria-label="Edit summary"
                size="small"
                onClick={handleToggleEdit}
                disabled={readOnly}
              >
                <Icon fontSize="small">edit</Icon>
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}
    </div>
  );

  return (
    <div className="editable-summary">
      {showEditButton &&
        (renderHeader
          ? renderHeader({
              isEditing,
              isDirty,
              onToggleEdit: handleToggleEdit,
              onCopy: handleCopy,
              copyTooltipTitle,
              readOnly,
              aggregatedForDisplay,
            })
          : defaultHeader)}
      {saveError && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          {saveError}
        </Typography>
      )}

      <div className="editable-summary-content">
        {isEditing ? (
          <div className="mp-block artifact-content">
            <MarkdownEditor value={draftValue} onChange={setDraftValue} showCardToolbar={false} />
          </div>
        ) : (
          <div
            role="button"
            tabIndex={0}
            onDoubleClick={handleDoubleClick}
            onKeyDown={handlePreviewKeyDown}
            aria-label="Edit summary"
            className="editable-summary-preview mp-block artifact-content"
          >
            <SlackishMarkdown text={aggregatedForDisplay} className="editable-summary-view" />
          </div>
        )}
      </div>

      <Dialog open={showConfirmModal} onClose={handleCancelDiscard} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', paddingBottom: '16px' }}>
          Your Document is Not Saved
        </DialogTitle>
        <DialogContent sx={{ paddingBottom: '24px' }}>
          <p style={{ margin: '0.25rem 0' }}>
            By navigating away from this page, you will lose the changes that were made.
          </p>
        </DialogContent>
        <DialogActions>
          <Button color="gray" variant="outlined" onClick={handleCancelDiscard}>
            Cancel
          </Button>
          <Button color="warning" variant="contained" onClick={handleConfirmDiscard}>
            Leave Without Saving
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
